import {
  Box,
  Card,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EnergyReportPanel from "./EnergyReportPanel";
import { PageHeader } from "./PageHeader";
import { foyerApi } from "../http/foyer";
import { PermissionGate } from "../auth/access";
import { errorText } from "../utils/apiError";
import type { EnergyRange, EnergyReport } from "@shared/types/foyer";

const RANGES: EnergyRange[] = ["day", "week", "month"];

/**
 * Premise-scoped energy + CO₂ overview, shown inside the premise editor next to
 * the power schedules that drive the savings. This is the customer-facing ESG
 * surface - a hotel sees its own building's consumption, not a fleet-wide
 * total. Reads the energy endpoint narrowed to this premise.
 */
export default function EnergyView({ premiseId }: { premiseId: string }) {
  return (
    <PermissionGate resource="screens" level="view" place={{ premiseId }}>
      <PremiseEnergy premiseId={premiseId} />
    </PermissionGate>
  );
}

function PremiseEnergy({ premiseId }: { premiseId: string }) {
  const [data, setData] = useState<EnergyReport | null>(null);
  const [range, setRange] = useState<EnergyRange>("week");
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation(["analytics", "common"]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await foyerApi.fleetEnergy(range, { premiseId });
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(errorText(err, t));
      }
    }
    load();
    const timer = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [range, premiseId, t]);

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t("energyView.eyebrow")}
        title={t("energyView.title")}
        help={t("energyView.help")}
        actions={
          <Select
            size="small"
            value={range}
            onChange={(e) => setRange(e.target.value as EnergyRange)}
            sx={{ minWidth: 130, bgcolor: "background.paper" }}
            data-testid="energy-range"
          >
            {RANGES.map((r) => (
              <MenuItem key={r} value={r}>
                {t(`common:labels.${r}`)}
              </MenuItem>
            ))}
          </Select>
        }
      />

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {data ? (
        <EnergyReportPanel report={data} scope="premise" />
      ) : (
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ p: 3 }}>
                <Box sx={{ height: 88 }} />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}
